<script setup>
import PageHero from '@/views/front-pages/PageHero.vue'
import PublicPageShell from '@/views/front-pages/PublicPageShell.vue'
import { useApi } from '@/utils/api'
import { useConfigStore } from '@core/stores/config'

definePage({ meta: { layout: 'blank', public: true } })

const store = useConfigStore()

store.skin = 'default'

const route = useRoute()

// Addendum 15, Modul BR: GET /api/public/blog/{slug} HANYA mengembalikan
// artikel status=published (404 untuk draft/slug tidak ada, lihat
// Public\BlogController::show()) -- notFound di bawah menangani KEDUA
// kasus itu dengan tampilan yang sama, sengaja tidak membedakan "slug
// salah" vs "ini draft" supaya tidak membocorkan info soal artikel mana
// yang sedang draft ke pengunjung publik.
const post = ref(null)
const loading = ref(true)
const notFound = ref(false)

const fetchPost = async () => {
  loading.value = true
  notFound.value = false
  try {
    const response = await useApi(`/api/public/blog/${route.params.slug}`).get()
    const body = response?.data ?? response

    post.value = body?.data ?? body
  } catch (err) {
    if ((err?.status ?? err?.response?.status) === 404)
      notFound.value = true
    else
      console.error('❌ Gagal memuat artikel:', err)
    post.value = null
  } finally {
    loading.value = false
  }
}

onMounted(fetchPost)

const formatDate = value => {
  if (!value) return ''

  return new Date(value).toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' })
}
</script>

<template>
  <PublicPageShell>
    <template v-if="loading">
      <section class="py-16 text-center">
        <VProgressCircular
          indeterminate
          color="primary"
        />
      </section>
    </template>

    <template v-else-if="notFound || !post">
      <PageHero
        eyebrow="Blog"
        eyebrow-icon="ri-article-line"
        title="Artikel Tidak Ditemukan"
        subtitle="Artikel yang Anda cari mungkin sudah dipindahkan atau belum tersedia."
      />
      <section class="pb-16">
        <VContainer class="text-center">
          <VBtn
            :to="{ name: 'blog' }"
            variant="tonal"
            color="primary"
            prepend-icon="ri-arrow-left-line"
          >
            Kembali ke Blog
          </VBtn>
        </VContainer>
      </section>
    </template>

    <template v-else>
      <section class="blog-detail-hero py-16">
        <VContainer>
          <div class="mx-auto blog-detail-inner">
            <VChip
              color="primary"
              variant="tonal"
              size="small"
              class="mb-4"
            >
              {{ formatDate(post.published_at) }}
            </VChip>
            <h1 class="blog-detail-title mb-6">
              {{ post.title }}
            </h1>
            <VImg
              v-if="post.featured_image_url"
              :src="post.featured_image_url"
              rounded="lg"
              cover
              max-height="420"
              class="mb-2"
            />
          </div>
        </VContainer>
      </section>

      <section class="pb-16">
        <VContainer>
          <div class="mx-auto blog-detail-inner">
            <div class="blog-detail-content">
              {{ post.content }}
            </div>

            <VDivider class="my-10" />

            <div class="text-center">
              <VBtn
                :to="{ name: 'blog' }"
                variant="tonal"
                color="primary"
                prepend-icon="ri-arrow-left-line"
              >
                Lihat Artikel Lainnya
              </VBtn>
            </div>
          </div>
        </VContainer>
      </section>
    </template>
  </PublicPageShell>
</template>

<style lang="scss" scoped>
.blog-detail-hero {
  background: rgba(var(--v-theme-surface), 1);
}

.blog-detail-inner {
  max-inline-size: 760px;
}

.blog-detail-title {
  font-size: clamp(1.75rem, 4vw, 2.5rem);
  font-weight: 800;
  letter-spacing: -0.02em;
  line-height: 1.25;
}

.blog-detail-content {
  font-size: 1.0625rem;
  line-height: 1.8;
  color: rgba(var(--v-theme-on-surface), var(--v-high-emphasis-opacity));
  white-space: pre-line;
}
</style>
